import { useEffect, useRef, useCallback } from "react";
import {
  createMetricsEvent,
  updateMetricsEvent,
  sendMetricsBeacon,
} from "../services/MeviApiService";

const INACTIVITY_TIMEOUT = 60_000; // 1 minute
const PAUSE_INTERVAL = 60_000; // 1 minute

/**
 * Tracks form-editing lifecycle events for time-tracking metrics.
 *
 * Events: load, dirty, pause, resume, save, close
 * Pause/resume detected via 1-minute inactivity after a dirty transition.
 *
 * @param {Object} props
 * @param {string|null} props.studioFormId
 * @param {string|null} props.studioFormDataId
 * @param {string|null} props.profileId
 * @param {boolean} props.formJsonDirty
 * @param {boolean} props.formRulesDirty
 * @param {boolean} props.offerCompareDirty
 * @returns {{ trackSave: () => void, trackLoad: (source: string) => void }}
 */
export const useMetricsTracker = ({
  studioFormId,
  studioFormDataId,
  profileId,
  formJsonDirty,
  formRulesDirty,
  offerCompareDirty,
}) => {
  const prevDirtyRef = useRef({ form: false, rules: false, compare: false });
  const inactivityTimerRef = useRef(null);
  const pauseIntervalRef = useRef(null);
  const pauseEventIdRef = useRef(null);
  const isPausedRef = useRef(false);
  const pauseDurationRef = useRef(0);
  const loadTrackedRef = useRef(null); // tracks which studioFormDataId we sent load for
  // Stable refs for values needed in beforeunload
  const idsRef = useRef({ studioFormId, studioFormDataId, profileId });
  idsRef.current = { studioFormId, studioFormDataId, profileId };

  const canTrack = studioFormId && studioFormDataId && profileId;

  const fireEvent = useCallback(
    async (event) => {
      if (!canTrack) return null;
      try {
        const result = await createMetricsEvent(studioFormId, studioFormDataId, {
          profileId,
          ...event,
        });
        return result;
      } catch (err) {
        console.error("Metrics event failed:", err);
        return null;
      }
    },
    [studioFormId, studioFormDataId, profileId, canTrack],
  );

  const updatePause = useCallback(
    async (eventId, duration) => {
      if (!canTrack || !eventId) return;
      try {
        await updateMetricsEvent(studioFormId, studioFormDataId, eventId, {
          profileId,
          duration,
        });
      } catch (err) {
        console.error("Metrics pause update failed:", err);
      }
    },
    [studioFormId, studioFormDataId, profileId, canTrack],
  );

  const clearPauseState = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (pauseIntervalRef.current) {
      clearInterval(pauseIntervalRef.current);
      pauseIntervalRef.current = null;
    }
    isPausedRef.current = false;
    pauseEventIdRef.current = null;
    pauseDurationRef.current = 0;
  }, []);

  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(async () => {
      // Enter pause state
      isPausedRef.current = true;
      pauseDurationRef.current = 1;
      const result = await fireEvent({ type: "pause", duration: 1 });
      if (result?.id) {
        pauseEventIdRef.current = result.id;
        // Start interval to update duration each minute
        pauseIntervalRef.current = setInterval(async () => {
          pauseDurationRef.current += 1;
          await updatePause(pauseEventIdRef.current, pauseDurationRef.current);
        }, PAUSE_INTERVAL);
      }
    }, INACTIVITY_TIMEOUT);
  }, [fireEvent, updatePause]);

  // Reset load guard when studioFormDataId changes
  useEffect(() => {
    loadTrackedRef.current = null;
  }, [studioFormDataId]);

  // Dirty detection — watch for false→true transitions
  useEffect(() => {
    if (!canTrack) return;

    const prev = prevDirtyRef.current;
    const transitions = [];

    if (formJsonDirty && !prev.form) transitions.push("form");
    if (formRulesDirty && !prev.rules) transitions.push("rules");
    if (offerCompareDirty && !prev.compare) transitions.push("compare");

    prevDirtyRef.current = {
      form: formJsonDirty,
      rules: formRulesDirty,
      compare: offerCompareDirty,
    };

    if (transitions.length === 0) return;

    // If paused, fire resume and clear pause state
    if (isPausedRef.current) {
      fireEvent({ type: "resume" });
      if (pauseIntervalRef.current) {
        clearInterval(pauseIntervalRef.current);
        pauseIntervalRef.current = null;
      }
      isPausedRef.current = false;
      pauseEventIdRef.current = null;
      pauseDurationRef.current = 0;
    }

    // Fire dirty events for each transition
    for (const dirtyType of transitions) {
      fireEvent({ type: "dirty", dirtyType });
    }

    // Reset inactivity timer
    startInactivityTimer();
  }, [formJsonDirty, formRulesDirty, offerCompareDirty, canTrack, fireEvent, startInactivityTimer]);

  // Send close event via beacon when browser/tab closes
  useEffect(() => {
    const sendClose = () => {
      const { studioFormId: sfId, studioFormDataId: sfdId, profileId: pId } = idsRef.current;
      if (!sfId || !sfdId || !pId) return;

      const body = { profileId: pId, type: "close" };
      if (isPausedRef.current) {
        body.duration = pauseDurationRef.current;
      }
      sendMetricsBeacon(sfId, sfdId, body);
    };

    const handlePageHide = (e) => {
      // persisted = page is going into bfcache (not truly closing), skip
      if (e.persisted) return;
      sendClose();
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", sendClose);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", sendClose);
    };
  }, []);

  // Cleanup timers on unmount or ID change
  useEffect(() => {
    return () => clearPauseState();
  }, [studioFormId, studioFormDataId, clearPauseState]);

  // Imperative: call after successful cloud save
  const trackSave = useCallback(() => {
    fireEvent({ type: "save" });
    // Reset inactivity timer since save is an activity
    startInactivityTimer();
  }, [fireEvent, startInactivityTimer]);

  // Imperative: call with source ("cloud" or "local") at each load site
  const trackLoad = useCallback(
    (source) => {
      if (loadTrackedRef.current === idsRef.current.studioFormDataId) return;
      loadTrackedRef.current = idsRef.current.studioFormDataId;
      fireEvent({ type: "load", source });
    },
    [fireEvent],
  );

  return { trackSave, trackLoad };
};
